import { combineReducers, createSelector, EnhancedStore, Reducer, Slice } from '@reduxjs/toolkit';
import { reducers } from './reducers';

export class DynamicStoreManager {
  store: EnhancedStore;
  rtkSlices: Record<string, Slice>;
  asyncReducers: Record<string, Reducer>;

  constructor(store: EnhancedStore) {
    // Initial store configuration
    this.store = store;
    this.rtkSlices = {};
    this.asyncReducers = {};
  }

  getDynamicSlice(key: string): Slice {
    return this.rtkSlices[key];
  }

  selectSliceState<T = unknown, R = unknown>(key: string): (state: T) => R {
    return state => {
      const parts = key.split('/');
      return parts.reduce((acc, part) => {
        if (acc && typeof acc === 'object' && part in acc) {
          return acc[part];
        }
        return undefined;
      }, state) as R;
    };
  }

  makeSelectSliceStates = (keys: string[]) =>
    createSelector(
      keys.map(key => this.selectSliceState(key)),
      (...selectedSliceStates) =>
        selectedSliceStates.reduce((result, state, index) => {
          const key = keys[index];
          return {
            ...(result as any),
            [key]: state,
          };
        }, {}),
    );

  injectSlice(key: string, rtkSlice: Slice) {
    this.rtkSlices[key] = rtkSlice;
    this.asyncReducers[key] = rtkSlice.reducer;
    this.updateReducers();
  }

  removeSlice(key: string) {
    if (key in this.rtkSlices) {
      delete this.rtkSlices[key];
      delete this.asyncReducers[key];
      this.updateReducers();
    }
  }

  private unflatten(reducers) {
    return Object.entries(reducers).reduce((acc, [key, reducer]) => {
      const parts = key.split('/');
      parts.reduce((nestedAcc, part, index) => {
        if (index === parts.length - 1) {
          nestedAcc[part] = reducer;
        } else {
          nestedAcc[part] = nestedAcc[part] ?? {};
        }
        return nestedAcc[part];
      }, acc);
      return acc;
    }, {});
  }

  private recursiveCombineReducers(reducers) {
    return Object.keys(reducers).reduce((combined, key) => {
      const reducer = reducers[key];
      const combinedReducer =
        typeof reducer === 'object' ? combineReducers(this.recursiveCombineReducers(reducer)) : reducer;
      return { ...combined, [key]: combinedReducer };
    }, {});
  }

  private updateReducers() {
    const treeReducers = this.unflatten(this.asyncReducers);
    const combinedTreeReducers = this.recursiveCombineReducers(treeReducers);

    this.store.replaceReducer(
      combineReducers({
        ...reducers,
        ...combinedTreeReducers,
      }),
    );
  }
}
